import { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useUsersMeQuery } from '@/gql/systemApi'
import { IS_LOGGED_IN } from '@/apollo/queries'
import { SignInQueryResult } from '@/apollo/AuthRoute'
import { clearRumUser, identifyRumUser } from '@/utils/datadog'

/**
 * Null-rendering watcher that keeps Datadog RUM's user identity in sync with
 * the auth state (ENG-1634): identifies the user on sign-in and on
 * authenticated boot, clears the identity on sign-out. Mounted once inside
 * ApolloProvider (it only needs Apollo, and must not be gated behind
 * providers that block rendering while loading).
 *
 * Uses the same reactive IS_LOGGED_IN local query + UsersMe pattern as
 * OptimizelyProvider, so no new data fetching is introduced: UsersMe is
 * already queried at boot and served from the Apollo cache.
 */
const RumUserIdentity = () => {
  const { data } = useQuery<SignInQueryResult>(IS_LOGGED_IN)
  const isLoggedIn = Boolean(data?.isLoggedIn)
  const { data: userMe } = useUsersMeQuery({ skip: !isLoggedIn })

  useEffect(() => {
    if (!isLoggedIn) {
      clearRumUser()
      return
    }

    const user = userMe?.users_me
    if (!user?.id) return

    const name = [user.first_name, user.last_name].filter(Boolean).join(' ')

    identifyRumUser({
      id: user.id,
      email: user.email ?? undefined,
      name: name || undefined,
      orgId: user.current_organization?.[0]?.organization_id?.id ?? undefined,
    })
  }, [isLoggedIn, userMe])

  return null
}

export default RumUserIdentity
